import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, ShieldAlert, Upload, Trash2, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import DataPorting from "@/components/DataPorting";
import { FeatureSwitches, DocumentNumbering, Preferences } from "@/components/OrgControls";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const TABS = [
  ["business", "Business"],
  ["documents", "Tax & documents"],
  ["numbering", "Numbering"],
  ["features", "Features"],
  ["preferences", "Preferences"],
  ["data", "Import & export"],
  ["users", "Users"],
];

// Only these two tabs are edited through the org form and its Save button.
const FORM_TABS = new Set(["business", "documents"]);

export default function Settings() {
  const { role, setOrganization, reload } = useAuth();
  const [tab, setTab] = useState("business");
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [states, setStates] = useState([]);
  const [gstinInfo, setGstinInfo] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [logo, setLogo] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const readOnly = role !== "OWNER";

  useEffect(() => {
    api.get("/organization").then(({ data }) => setOrg(data));
    api.get("/members").then(({ data }) => setMembers(data));
    api.get("/masters/states").then(({ data }) => setStates(data));
    api.get("/invoice-templates").then(({ data }) => setTemplates(data.templates));
    // Fetched separately: the data URI is far too large to ride along on every
    // /organization response.
    api.get("/organization/logo").then(({ data }) => setLogo(data.logo));
  }, []);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";                       // let the same file be re-picked
    setLogoBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/organization/logo", body);
      setLogo(data.logo);
      toast.success("Logo updated — it will appear on new invoice PDFs");
    } catch (err) {
      toast.error(apiError(err));
    }
    setLogoBusy(false);
  };

  const removeLogo = async () => {
    try {
      await api.delete("/organization/logo");
      setLogo(null);
      toast.success("Logo removed");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const set = (k) => (e) => setOrg((o) => ({ ...o, [k]: e.target.value }));

  const checkGstin = async (value) => {
    setOrg((o) => ({ ...o, gstin: value.toUpperCase() }));
    if (value.length === 15) {
      const { data } = await api.get(`/masters/gstin/${value.toUpperCase()}`);
      setGstinInfo(data);
    } else setGstinInfo(null);
  };

  const save = async () => {
    try {
      const payload = {
        name: org.name, legal_name: org.legal_name, gstin: org.gstin || null,
        registration_type: org.registration_type, state_code: org.state_code,
        address_line1: org.address_line1, city: org.city, pincode: org.pincode,
        email: org.email, phone: org.phone, bank_name: org.bank_name,
        bank_account_number: org.bank_account_number, bank_ifsc: org.bank_ifsc,
        upi_id: org.upi_id, round_off_mode: org.round_off_mode,
        default_due_days: Number(org.default_due_days) || 15,
        invoice_template: org.invoice_template || "CLASSIC",
        einvoice_settings: org.einvoice_settings || undefined,
        invoice_accent_color: org.invoice_accent_color || null,
        signature_label: org.signature_label || null,
        terms_and_conditions: org.terms_and_conditions, aato_band: org.aato_band,
      };
      const { data } = await api.patch("/organization", payload);
      setOrg(data);
      setOrganization(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!org) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Settings</h1>
          <p className="text-sm text-slate-500 mt-1">
            Identity and tax profile, document numbering, which features are switched
            on, and how the system behaves
          </p>
        </div>
        {!readOnly && FORM_TABS.has(tab) && (
          <Button onClick={save} data-testid="settings-save" className="gap-1.5 bg-blue-600 hover:bg-blue-700">
            <Save size={16} /> Save changes
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map(([key, label]) => (
          <button key={key} data-testid={`settings-tab-${key}`} onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-150 ${
              tab === key ? "border-blue-600 text-blue-700"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {readOnly && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 flex gap-2" data-testid="settings-readonly">
          <ShieldAlert size={15} className="shrink-0 mt-0.5" />
          Your role is read-only for organisation settings. Ask the Owner to make changes.
        </div>
      )}

      {tab === "business" && (<>
      <Section title="Business identity">
        <FieldRow>
          <Field label="Trade name"><Input data-testid="org-name" disabled={readOnly} value={org.name || ""} onChange={set("name")} /></Field>
          <Field label="Legal name"><Input data-testid="org-legal-name" disabled={readOnly} value={org.legal_name || ""} onChange={set("legal_name")} /></Field>
        </FieldRow>
        <FieldRow>
          <Field label="GSTIN">
            <Input data-testid="org-gstin" className="font-mono uppercase" disabled={readOnly} value={org.gstin || ""}
              onChange={(e) => checkGstin(e.target.value)} />
            {gstinInfo && (
              <div className={`text-[11px] mt-1 ${gstinInfo.valid ? "text-emerald-700" : "text-rose-700"}`} data-testid="org-gstin-feedback">
                {gstinInfo.valid ? `Valid · ${gstinInfo.state_name} · PAN ${gstinInfo.pan}` : gstinInfo.reason}
              </div>
            )}
          </Field>
          <Field label="Registration type">
            <Select value={org.registration_type} disabled={readOnly}
              onValueChange={(v) => setOrg((o) => ({ ...o, registration_type: v }))}>
              <SelectTrigger data-testid="org-registration-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="REGULAR">Regular — Tax Invoice</SelectItem>
                <SelectItem value="COMPOSITION">Composition — Bill of Supply, no GST</SelectItem>
                <SelectItem value="UNREGISTERED">Unregistered</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </FieldRow>
        <FieldRow>
          <Field label="Address"><Input data-testid="org-address" disabled={readOnly} value={org.address_line1 || ""} onChange={set("address_line1")} /></Field>
          <Field label="City"><Input data-testid="org-city" disabled={readOnly} value={org.city || ""} onChange={set("city")} /></Field>
        </FieldRow>
        <FieldRow>
          <Field label="Pincode"><Input data-testid="org-pincode" className="font-mono" disabled={readOnly} value={org.pincode || ""} onChange={set("pincode")} /></Field>
          <Field label="State">
            <Select value={org.state_code} disabled={readOnly} onValueChange={(v) => setOrg((o) => ({ ...o, state_code: v }))}>
              <SelectTrigger data-testid="org-state"><SelectValue /></SelectTrigger>
              <SelectContent>{states.map((s) => <SelectItem key={s.code} value={s.code}>{s.name} ({s.code})</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </FieldRow>
        <FieldRow>
          <Field label="Email"><Input data-testid="org-email" disabled={readOnly} value={org.email || ""} onChange={set("email")} /></Field>
          <Field label="Phone"><Input data-testid="org-phone" className="font-mono" disabled={readOnly} value={org.phone || ""} onChange={set("phone")} /></Field>
        </FieldRow>
      </Section>

      </>)}

      {tab === "documents" && (<>
      <Section title="Tax & document defaults">
        <FieldRow>
          <Field label="Turnover band (drives HSN digits)">
            <Select value={org.aato_band} disabled={readOnly} onValueChange={(v) => setOrg((o) => ({ ...o, aato_band: v }))}>
              <SelectTrigger data-testid="org-aato"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="UPTO_5CR">Up to ₹5 crore — 4 digit HSN</SelectItem>
                <SelectItem value="ABOVE_5CR">Above ₹5 crore — 6 digit HSN (e-Invoice applies)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Round off">
            <Select value={org.round_off_mode} disabled={readOnly} onValueChange={(v) => setOrg((o) => ({ ...o, round_off_mode: v }))}>
              <SelectTrigger data-testid="org-roundoff"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">None</SelectItem>
                <SelectItem value="NEAREST_RUPEE">Nearest rupee</SelectItem>
                <SelectItem value="UP">Always up</SelectItem>
                <SelectItem value="DOWN">Always down</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </FieldRow>
        <FieldRow>
          <Field label="Default due days"><Input data-testid="org-due-days" className="font-mono" disabled={readOnly}
            value={org.default_due_days ?? 15} onChange={set("default_due_days")} /></Field>
          <Field label="Invoice series">
            <button onClick={() => setTab("numbering")} data-testid="org-series"
              className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-left text-sm text-slate-600 hover:border-slate-300 hover:text-slate-900 transition-colors duration-150">
              Set your own prefix and starting number →
            </button>
          </Field>
        </FieldRow>
        <Field label="Terms & conditions printed on invoices">
          <Textarea rows={3} data-testid="org-terms" disabled={readOnly} value={org.terms_and_conditions || ""} onChange={set("terms_and_conditions")} />
        </Field>
        {org.aato_band === "ABOVE_5CR" && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="einvoice-warning">
            Above the e-Invoice AATO threshold, registering an IRN is mandatory. Set up your
            IRP credentials below, then generate the IRN from each invoice before you send it.
          </div>
        )}
      </Section>

      <Section title="Invoice branding">
        <Field label="Logo printed on invoices">
          <div className="flex flex-wrap items-center gap-4">
            <div className="grid h-20 w-40 place-items-center overflow-hidden rounded-md border border-dashed border-slate-300 bg-slate-50">
              {logo ? (
                <img src={logo} alt="Invoice logo" className="max-h-full max-w-full object-contain"
                  data-testid="org-logo-preview" />
              ) : (
                <span className="text-[11px] text-slate-400">No logo</span>
              )}
            </div>
            {!readOnly && (
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium transition-colors duration-150 hover:bg-slate-50">
                  <Upload size={13} /> {logoBusy ? "Uploading…" : logo ? "Replace" : "Upload logo"}
                  <input type="file" className="hidden" accept="image/png,image/jpeg,image/gif,image/webp"
                    data-testid="org-logo-input" onChange={uploadLogo} disabled={logoBusy} />
                </label>
                {logo && (
                  <button onClick={removeLogo} data-testid="org-logo-remove"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-600">
                    <Trash2 size={13} /> Remove
                  </button>
                )}
                <span className="text-[11px] text-slate-500">PNG, JPEG, GIF or WebP · up to 512 KB</span>
              </div>
            )}
          </div>
        </Field>

        <Field label="Invoice template">
          <div className="grid gap-3 sm:grid-cols-3" data-testid="template-picker">
            {templates.map((t) => {
              const selected = (org.invoice_template || "CLASSIC") === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  disabled={readOnly}
                  data-testid={`template-${t.key}`}
                  onClick={() => setOrg((o) => ({ ...o, invoice_template: t.key }))}
                  className={`rounded-md border p-3 text-left transition-colors duration-150 ${
                    selected ? "border-blue-600 bg-blue-50/50 ring-1 ring-blue-600" : "border-slate-200 hover:border-slate-400"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">{t.label}</span>
                    {selected && <Check size={14} className="text-blue-600" />}
                  </div>
                  {/* Miniature of the header treatment each template uses. */}
                  <div className="mt-2 space-y-1 rounded border border-slate-200 bg-white p-1.5">
                    <div className="h-2.5 w-full rounded-sm"
                      style={{ background: t.key === "MODERN" ? t.accent : "transparent" }} />
                    {t.key === "CLASSIC" && <div className="h-0.5 w-full" style={{ background: t.accent }} />}
                    <div className="h-1 w-3/4 rounded-sm bg-slate-200" />
                    <div className="h-1 w-1/2 rounded-sm bg-slate-100" />
                  </div>
                </button>
              );
            })}
          </div>
        </Field>

        <FieldRow>
          <Field label="Accent colour (blank uses the template default)">
            <div className="flex items-center gap-2">
              <Input type="color" className="h-9 w-14 p-1" disabled={readOnly}
                data-testid="org-accent-color"
                value={org.invoice_accent_color || "#1d4ed8"}
                onChange={(e) => setOrg((o) => ({ ...o, invoice_accent_color: e.target.value }))} />
              <Input className="font-mono" disabled={readOnly} placeholder="#1d4ed8"
                data-testid="org-accent-hex"
                value={org.invoice_accent_color || ""}
                onChange={(e) => setOrg((o) => ({ ...o, invoice_accent_color: e.target.value }))} />
              {org.invoice_accent_color && !readOnly && (
                <button className="text-xs text-slate-500"
                  onClick={() => setOrg((o) => ({ ...o, invoice_accent_color: "" }))}>Reset</button>
              )}
            </div>
          </Field>
          <Field label="Signature line">
            <Input data-testid="org-signature" disabled={readOnly}
              placeholder="Authorised Signatory"
              value={org.signature_label || ""} onChange={set("signature_label")} />
          </Field>
        </FieldRow>
      </Section>

      <Section title="e-Invoicing (IRN)">
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
          Mandatory for B2B supplies once your turnover crosses the e-Invoicing threshold.
          You need IRP credentials from NIC or your GSP. Leave this off until you have them —
          invoices are unaffected.
        </div>
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>
            <span className="font-medium">Enable e-Invoicing</span>
            <span className="block text-[11px] text-slate-500">
              Adds a &ldquo;Generate IRN&rdquo; action to issued B2B tax invoices.
            </span>
          </span>
          <Switch data-testid="einvoice-enabled" disabled={readOnly}
            checked={!!org.einvoice_settings?.enabled}
            onCheckedChange={(v) =>
              setOrg((o) => ({ ...o, einvoice_settings: { ...(o.einvoice_settings || {}), enabled: v } }))} />
        </label>
        {org.einvoice_settings?.enabled && (
          <>
            <FieldRow>
              <Field label="IRP username">
                <Input data-testid="einvoice-username" disabled={readOnly}
                  value={org.einvoice_settings?.username || ""}
                  onChange={(e) => setOrg((o) => ({ ...o, einvoice_settings: { ...o.einvoice_settings, username: e.target.value } }))} />
              </Field>
              <Field label={org.einvoice_settings?.password_set
                ? "IRP password (stored — leave blank to keep)" : "IRP password"}>
                <Input data-testid="einvoice-password" type="password" disabled={readOnly}
                  placeholder={org.einvoice_settings?.password_set ? "••••••••" : ""}
                  value={org.einvoice_settings?.password || ""}
                  onChange={(e) => setOrg((o) => ({ ...o, einvoice_settings: { ...o.einvoice_settings, password: e.target.value } }))} />
              </Field>
            </FieldRow>
            <Field label="IRP endpoint">
              <Select value={org.einvoice_settings?.base_url || "https://einv-apisandbox.nic.in"}
                disabled={readOnly}
                onValueChange={(v) => setOrg((o) => ({ ...o, einvoice_settings: { ...o.einvoice_settings, base_url: v } }))}>
                <SelectTrigger data-testid="einvoice-endpoint"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="https://einv-apisandbox.nic.in">NIC Sandbox — test only</SelectItem>
                  <SelectItem value="https://einvoice1.gst.gov.in">NIC Production — live IRNs</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
              Start on the sandbox and register one invoice end to end before switching to
              production. A production IRN is a live filing and can only be cancelled within
              24 hours.
            </div>
          </>
        )}
      </Section>

      </>)}

      {tab === "business" && (
      <Section title="Bank details printed on invoices">
        <FieldRow>
          <Field label="Bank name"><Input data-testid="org-bank-name" disabled={readOnly} value={org.bank_name || ""} onChange={set("bank_name")} /></Field>
          <Field label="Account number"><Input data-testid="org-bank-account" className="font-mono" disabled={readOnly}
            value={org.bank_account_number || ""} onChange={set("bank_account_number")} /></Field>
        </FieldRow>
        <FieldRow>
          <Field label="IFSC"><Input data-testid="org-bank-ifsc" className="font-mono uppercase" disabled={readOnly}
            value={org.bank_ifsc || ""} onChange={set("bank_ifsc")} /></Field>
          <Field label="UPI ID"><Input data-testid="org-upi" className="font-mono" disabled={readOnly} value={org.upi_id || ""} onChange={set("upi_id")} /></Field>
        </FieldRow>
      </Section>

      )}

      {tab === "numbering" && (
        <Section title="Document numbering">
          <DocumentNumbering readOnly={readOnly} />
        </Section>
      )}

      {tab === "features" && (
        <Section title="Features">
          {/* The nav is driven by the session's module list, so the whole
              auth state is reloaded the moment a feature is switched. */}
          <FeatureSwitches readOnly={readOnly} onChanged={reload} />
        </Section>
      )}

      {tab === "preferences" && (
        <Section title="How the system behaves">
          <Preferences readOnly={readOnly} />
        </Section>
      )}

      {tab === "data" && (
        <Section title="Import &amp; export">
          <DataPorting />
        </Section>
      )}

      {tab === "users" && (
      <Section title="Users">
        <div className="divide-y divide-slate-100" data-testid="members-list">
          {members.map((m) => (
            <div key={m.id} className="flex items-center justify-between py-2.5">
              <div>
                <div className="text-sm font-medium">{m.name}</div>
                <div className="font-mono text-[11px] text-slate-500">{m.email}</div>
              </div>
              <span className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                {m.role}
              </span>
            </div>
          ))}
        </div>
      </Section>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="rounded-md border border-slate-200 bg-white">
    <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold uppercase tracking-widest text-slate-600">{title}</div>
    <div className="p-5 space-y-4">{children}</div>
  </div>
);

const FieldRow = ({ children }) => <div className="grid gap-4 sm:grid-cols-2">{children}</div>;

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-slate-600">{label}</Label>
    {children}
  </div>
);
